import React, { useState } from 'react';
import { Megaphone, X, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';

/**
 * The card shown in an ad space nobody has bought, and the request pop-up behind its button.
 * Only practitioners and vendors ever see it (enforced server-side), and only where the admin
 * has switched invitations on for that particular space.
 */
export function AvailableSpaceCard({ space, variant = 'banner', theme = 'dark', className = '' }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [imgBroken, setImgBroken] = useState(false);
  const token = localStorage.getItem('token');
  const dark = theme !== 'light';

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/spotlights/space-request`,
        { slot: space.slot, message },
        { headers: { Authorization: `Bearer ${token}` } });
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'That did not send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const shell = dark
    ? 'border-dashed border-white/15 hover:border-amber-400/40 bg-white/[0.02]'
    : 'border-dashed border-gray-300 hover:border-amber-400 bg-white';
  const head = dark ? 'text-white/80' : 'text-gray-800';
  const sub = dark ? 'text-white/45' : 'text-gray-500';
  const btn = dark
    ? 'bg-amber-500/15 text-amber-200 hover:bg-amber-500/25 border border-amber-400/30'
    : 'bg-amber-100 text-amber-800 hover:bg-amber-200 border border-amber-300';

  const isColumn = variant === 'card' || variant === 'flank';
  const isRail = variant === 'rail';
  const art = space.image_url && !imgBroken ? space.image_url : null;

  return (
    <>
      <div
        className={`rounded-xl border ${shell} transition-colors overflow-hidden ${isColumn ? 'flex flex-col' : 'flex items-stretch'} ${className}`}
        data-testid={`available-space-${space.slot}`}
      >
        {art && (
          <img src={art} alt="" onError={() => setImgBroken(true)}
            className={isColumn ? 'w-full h-32 object-cover opacity-80'
              : `${isRail ? 'w-16' : 'w-28 sm:w-40'} object-cover opacity-80`} />
        )}
        <div className={`flex-1 min-w-0 p-4 ${isColumn ? 'text-center flex flex-col gap-2' : 'flex items-center gap-4'}`}>
          {!art && (
            <Megaphone className={`w-5 h-5 flex-shrink-0 ${dark ? 'text-amber-300/70' : 'text-amber-600'} ${isColumn ? 'mx-auto' : ''}`} />
          )}
          <div className={`min-w-0 flex-1 ${isColumn ? '' : 'text-left'}`}>
            <p className={`font-medium text-sm ${head}`}>{space.headline}</p>
            {space.body && <p className={`text-xs mt-0.5 ${sub}`}>{space.body}</p>}
          </div>
          <button
            type="button"
            onClick={() => { setOpen(true); setSent(false); }}
            className={`flex-shrink-0 text-xs font-medium px-3 py-1.5 rounded-full transition-colors ${btn}`}
            data-testid={`available-space-cta-${space.slot}`}
          >
            {space.cta_label}
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4"
          onClick={() => setOpen(false)}>
          <div className="w-full max-w-md rounded-2xl bg-[#14161c] border border-white/10 p-6"
            onClick={(e) => e.stopPropagation()} data-testid="space-request-modal">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-white">Request this space</h3>
                <p className="text-xs text-white/50 mt-1">{space.slot_label}</p>
              </div>
              <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white"
                data-testid="space-request-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            {sent ? (
              <div className="mt-5" data-testid="space-request-sent">
                <p className="text-sm text-white/80">
                  Thank you — your request is with the team. If the space suits what you want to
                  promote, you will get an email with everything you need to set it up.
                </p>
                <button onClick={() => setOpen(false)}
                  className="mt-4 w-full rounded-full bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium py-2.5">
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="mt-5 space-y-3">
                <label className="block text-xs text-white/50">
                  What would you like to promote here? <span className="text-white/30">(optional)</span>
                </label>
                <textarea
                  rows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="A workshop, a product, my sessions…"
                  className="w-full rounded-lg bg-white/5 border border-white/10 text-sm text-white p-3 outline-none focus:border-amber-400/50"
                  data-testid="space-request-message"
                />
                <p className="text-[11px] text-white/40">
                  This is a request, not a booking. Nothing is charged and nothing goes live until
                  it has been approved.
                </p>
                <button type="submit" disabled={sending}
                  className="w-full rounded-full bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white text-sm font-medium py-2.5 flex items-center justify-center gap-2"
                  data-testid="space-request-submit">
                  {sending && <Loader2 className="w-4 h-4 animate-spin" />} Send request
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default AvailableSpaceCard;
